import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';

import {
    clearProductToEdit,
    clearServiceToEdit,
    closeAddProductModal,
    closeAddServiceModal,
    openAddProductModal,
    openAddServiceModal,
    setProductToEdit,
    setServiceToEdit,
} from '../../store/slices/purchases/excludedSubjectSlice';

import { useBasePurchase } from './useBasePurchase';
import { updateExcludedSubjectTotals } from '../../store/slices/purchases/basePurchaseSlice';
import ExcludedSubjectPurchaseService from '../../services/purchases/ExcludedSubjectPurchaseService';

export const useExcludedSubject = () => {
    const dispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { openModal } = useModals();
    const confirm = useConfirmModal();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

    const { addProduct, updateProduct, deleteProduct } = useBasePurchase();
    const { productToEdit, showAddProductModal, showEditProductModal, showAddServiceModal } = useSelector(
        (state: RootState) => state.excludedSubject
    );

    const handlerAddProduct = async (product: any) => {
        // console.log(product);
        try {
            showLoadingIndicator();
            const response = await ExcludedSubjectPurchaseService.addProduct(product);
            console.log(response);

            if (response.statusCode === 201) {
                const { title, message, type, data } = response;
                const { product: savedProduct, totals } = data;

                addProduct(savedProduct);
                dispatch(updateExcludedSubjectTotals(totals));

                // 🧠 Cerrar el modal correcto según el tipo
                if (savedProduct.type === 'PRODUCT') {
                    dispatch(closeAddProductModal());
                    dispatch(clearProductToEdit());
                } else if (savedProduct.type === 'SERVICE') {
                    dispatch(closeAddServiceModal());
                    dispatch(clearServiceToEdit());
                }

                openModal(type, { title, message });
            } else {
                showAlert('error', 'Error', 'No se pudo agregar el producto o servicio.');
            }
        } catch (error: any) {
            const response = error?.response?.data;
            openModal(response?.type || 'error', {
                title: response?.title || 'Error',
                message: response?.message || 'Ocurrió un error al agregar.',
            });
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerUpdateProduct = async (product: any) => {
        try {
            showLoadingIndicator();
            const response = await ExcludedSubjectPurchaseService.updateProduct(product);

            if (response.statusCode === 200) {
                const { title, message, type, data } = response;
                const { product: updatedProduct, totals } = data;

                updateProduct(updatedProduct);
                dispatch(updateExcludedSubjectTotals(totals));

                // 🧠 Cerrar el modal correcto
                if (updatedProduct.type === 'PRODUCT') {
                    dispatch(closeAddProductModal());
                    dispatch(clearProductToEdit());
                } else if (updatedProduct.type === 'SERVICE') {
                    dispatch(closeAddServiceModal());
                    dispatch(clearServiceToEdit());
                }

                openModal(type, { title, message });
            } else {
                showAlert('error', 'Error', 'No se pudo actualizar el producto o servicio.');
            }
        } catch (error) {
            showAlert('error', 'Error', 'Ocurrió un error inesperado al actualizar.');
        } finally {
            hideLoadingIndicator();
        }
    };


    const handlerDeleteProduct = async (id: number) => {
        confirm({
            title: '¿Eliminar?',
            message: '¿Seguro que deseas eliminar este ítem?',
            icon: 'warning',
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            confirmArgs: id,
            onConfirm: async (id) => {
                try {
                    showLoadingIndicator();
                    const response = await ExcludedSubjectPurchaseService.deleteProduct(id);

                    if (response.statusCode === 200) {
                        const { title, message, type, data } = response;

                        // 💡 data.totals viene del backend y debe incluir subtotal, retención y total
                        const { totals } = data;

                        deleteProduct(id);
                        dispatch(updateExcludedSubjectTotals(totals)); // <-- ✅ actualizamos totales globales

                       openModal(type, { title, message });
                    } else {
                        showAlert('error', 'Error', 'No se pudo eliminar.');
                    }
                } catch (error) {
                    showAlert('error', 'Error', 'Error inesperado al eliminar.');
                } finally {
                    hideLoadingIndicator();
                }
            },
        });
    };


    const handlerGetProductById = async (detailId: number) => {
        try {
            showLoadingIndicator();
            const response = await ExcludedSubjectPurchaseService.getProductById(detailId);
            if (response.statusCode === 200) {
                const item = response.data;
                if (item.type === 'PRODUCT') {
                    dispatch(setProductToEdit(item));
                    dispatch(openAddProductModal());
                } else if (item.type === 'SERVICE') {
                    dispatch(setServiceToEdit(item));
                    dispatch(openAddServiceModal());
                } else {
                    showAlert('error', 'Tipo desconocido', 'El tipo del ítem no es válido.');
                }
            } else {
                showAlert('error', 'Error', 'No se pudo obtener el detalle.');
            }
        } catch (error) {
            showAlert('error', 'Error', 'Ocurrió un error inesperado al obtener el detalle.');
        } finally {
            hideLoadingIndicator();
        }
    };


    return {
        productToEdit,
        showAddProductModal,
        showAddServiceModal,
        showEditProductModal,

        openAddProductModal: () => dispatch(openAddProductModal()),
        closeAddProductModal: () => dispatch(closeAddProductModal()),
        openAddServiceModal: () => dispatch(openAddServiceModal()),
        closeAddServiceModal: () => dispatch(closeAddServiceModal()),

        clearProductToEdit: () => dispatch(clearProductToEdit()),
        clearServiceToEdit: () => dispatch(clearServiceToEdit()),

        handleCancelProduct: () => {
            dispatch(closeAddProductModal());
            dispatch(clearProductToEdit());
        },

        handleCancelService: () => {
            dispatch(closeAddServiceModal());
            dispatch(clearServiceToEdit());
        },

        handlerAddProduct,
        handlerUpdateProduct,
        handlerDeleteProduct,
        handlerGetProductById,
    };
};
